import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Backend, PERMISSION} from '@generated-backend-types';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {takeUntil} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {DiagnosisService} from '../../../../services/api/diagnosis.service';
import {EmailService} from '../../../../services/api/administration/email.service';
import {
  MultiSelectWithServiceComponent
} from '../../../../components/form-inputs/multi-select-with-service/multi-select-with-service.component';
import {AddableTeamLeadersService} from '../../../../services/api/time-tracking/addable-team-leaders.service';
import {FormSubmitService} from '../../../../services/form-submit.service';
import {TimeTrackingEmployeeService} from '../../../../services/api/time-tracking/time-tracking-employee.service';
import {
  MultiSelectEnumComponent
} from '../../../../components/form-inputs/multi-select-enum/multi-select-enum.component';
import {
  DateTimeSelectorComponent
} from '../../../../components/form-inputs/date-time-selector/date-time-selector.component';
import {DateTime} from 'luxon';
import {WeeklyWorkingHoursComponent} from './weekly-working-hours/weekly-working-hours.component';
import {YearlyVacationDaysComponent} from './yearly-vacation-days/yearly-vacation-days.component';
import ModelStateEnum = Backend.App.Enums.ModelStateEnum;
import EmailTypeEnum = Backend.App.Enums.Administration.EmailTypeEnum;
import GermanStateEnum = Backend.App.Enums.TimeTracking.GermanStateEnum;
import TimeTrackingEmployee = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingEmployee.TimeTrackingEmployee;
import TimeTrackingEmployeeData = Backend.App.Data.Dashboard.TimeTracking.TimeTrackingEmployee.TimeTrackingEmployeeData;

@Component({
  selector: 'app-authenticated-time-tracking-employees-edit',
  templateUrl: './edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormGroupComponent,
    MultiSelectWithServiceComponent,
    MultiSelectEnumComponent,
    DateTimeSelectorComponent,
    WeeklyWorkingHoursComponent,
    YearlyVacationDaysComponent,
  ],
  standalone: true,
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public employee?: TimeTrackingEmployee;

  public form: StrictFormGroup<TimeTrackingEmployeeData> = this.fb.group({
    team_leader_id: this.fb.control(''),
    federal_state: this.fb.control<GermanStateEnum | null>(null),
    entry_date: this.fb.control(''),
  });

  public readonlyForm = this.fb.group({
    name: this.fb.control(''),
    email: this.fb.control(''),
  });

  protected readonly PERMISSION = PERMISSION;
  protected readonly ModelStateEnum = ModelStateEnum;
  protected readonly EmailTypeEnum = EmailTypeEnum;
  protected readonly GermanStateEnum = GermanStateEnum;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: NonNullableFormBuilder,
    protected readonly diagnosisService: DiagnosisService,
    protected emailService: EmailService,
    protected readonly addableTeamLeadersService: AddableTeamLeadersService,
    private readonly formSubmitService: FormSubmitService,
    private readonly employeeService: TimeTrackingEmployeeService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const employee: TimeTrackingEmployee | undefined = data['employee'];

      if (employee) {
        this.employee = employee;

        this.form.patchValue(employee);

        this.readonlyForm.patchValue({
          name: employee.user?.name ?? '',
          email: employee.user?.email ?? '',
        });
        this.readonlyForm.disable();

        this.cdr.markForCheck();
      }

      this.titleService.setTitle('authenticated.time_tracking.employees.edit.title');
    });
  }

  public save() {
    this.formSubmitService.submit<TimeTrackingEmployee, TimeTrackingEmployeeData, TimeTrackingEmployeeService>({
      form: this.form,
      service: this.employeeService,
      modelId: this.employee?.id,
      updatePermission: PERMISSION.TIMETRACKING_TIMETRACKINGEMPLOYEE_TIMETRACKINGEMPLOYEE_UPDATE_ALL,
      updateSuccessMessage: this.translate.instant('authenticated.time_tracking.employees.edit.success'),
      updateErrorMessage: this.translate.instant('authenticated.time_tracking.employees.edit.error'),
      dataTransformer: data => {
        if (!data.entry_date) return data;
        return {
          ...data,
          entry_date: DateTime.fromISO(data.entry_date).toISODate(),
        };
      }
    }).subscribe(employee => {
      if (!employee) return;
      this.employee = employee;
      this.cdr.markForCheck();
    });
  }
}
